import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { toaster } from "components/common/Toast"

const requestedFetchAssignedEquipments = createAction("equipmentAssignments/fetch/requested")
const receivedFetchAssignedEquipments = createAction("equipmentAssignments/fetch/received")
const failedFetchAssignedEquipments = createAction("equipmentAssignments/fetch/failed")

const requestedAssignEquipment = createAction("equipmentAssignments/assign/requested")
const receivedAssignEquipment = createAction("equipmentAssignments/assign/received")
const failedAssignEquipment = createAction("equipmentAssignments/assign/failed")

const requestedUnassignEquipment = createAction("equipmentAssignments/unassign/requested")
const receivedUnassignEquipment = createAction("equipmentAssignments/unassign/received")
const failedUnassignEquipment = createAction("equipmentAssignments/unassign/failed")

const initialState = {
  assignedEquipments: [],
  fetchOperation: {
    ...LOADING_INITIAL_STATE
  },
  assignOperation: {
    ...LOADING_INITIAL_STATE
  },
  unassignOperation: {
    ...LOADING_INITIAL_STATE
  }
}

const equipmentAssignmentsReducer = createReducer(initialState, {
  [requestedFetchAssignedEquipments.type]: (state) => {
    setRequested(state.fetchOperation)
  },
  [receivedFetchAssignedEquipments.type]: (state, { payload }) => {
    setReceived(state.fetchOperation)
    state.assignedEquipments = Array.isArray(payload) ? payload : (payload?.data || [])
  },
  [failedFetchAssignedEquipments.type]: (state) => {
    setFailed(state.fetchOperation)
  },

  [requestedAssignEquipment.type]: (state) => {
    setRequested(state.assignOperation)
  },
  [receivedAssignEquipment.type]: (state, { payload }) => {
    setReceived(state.assignOperation)
    const assigned = payload?.assigned_assets || []
    const failed = payload?.failed || []
    if (assigned.length) {
      toaster.success({ text: payload?.message || "Equipment assigned successfully" })
    }
    if (failed.length) {
      const reasons = failed.map((item) => item.error).filter(Boolean)
      toaster.error({ text: reasons.join(" ") || payload?.message || "Some assets could not be assigned" })
    }
  },
  [failedAssignEquipment.type]: (state) => {
    setFailed(state.assignOperation)
  },

  [requestedUnassignEquipment.type]: (state) => {
    setRequested(state.unassignOperation)
  },
  [receivedUnassignEquipment.type]: (state, { payload }) => {
    setReceived(state.unassignOperation)
    toaster.success({
      text: payload.message || "Equipment removed from booking"
    })
  },
  [failedUnassignEquipment.type]: (state) => {
    setFailed(state.unassignOperation)
  }
})

export default equipmentAssignmentsReducer

// Bulk assign — assets: [{ equipment_id, asset_id }, ...]
export const assignEquipmentsToBooking = (bookingId, assets) =>
  api({
    url: `/guide/bookings/${bookingId}/assign_equipment`,
    method: "post",
    data: { assets },
    onStart: requestedAssignEquipment.type,
    onSuccess: receivedAssignEquipment.type,
    onError: failedAssignEquipment.type
  })

// Single-asset convenience wrapper (used by the booking-details EquipmentTab)
export const assignEquipmentToBooking = (bookingId, equipmentId, assetId) =>
  assignEquipmentsToBooking(bookingId, [{ equipment_id: equipmentId, asset_id: assetId }])

export const unassignEquipmentFromBooking = (bookingId, equipmentId, assetId) =>
  api({
    url: `/guide/bookings/${bookingId}/unassign_equipment`,
    method: "delete",
    params: { equipment_id: equipmentId, asset_id: assetId },
    onStart: requestedUnassignEquipment.type,
    onSuccess: receivedUnassignEquipment.type,
    onError: failedUnassignEquipment.type
  })

export const fetchAssignedEquipments = (bookingId) =>
  api({
    url: `/guide/bookings/${bookingId}/assigned_equipments`,
    method: "get",
    onStart: requestedFetchAssignedEquipments.type,
    onSuccess: receivedFetchAssignedEquipments.type,
    onError: failedFetchAssignedEquipments.type
  })

export const selectAssignedEquipments = (state) => state.equipmentAssignments.assignedEquipments
export const selectFetchAssignedEquipmentsLoading = (state) => state.equipmentAssignments.fetchOperation.loading
export const selectAssignEquipmentLoading = (state) => state.equipmentAssignments.assignOperation.loading
export const selectUnassignEquipmentLoading = (state) => state.equipmentAssignments.unassignOperation.loading
